import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { useAdminLayoutContext } from '../../components/admin/AdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Building2,
  Users,
  X,
  Check,
  AlertTriangle,
  CheckCircle,
  Sliders,
  ArrowLeft,
  Mail,
  User,
} from 'lucide-react';

interface DepartmentWithUsage {
  id: string;
  created_at: string;
  company_id: string;
  name: string;
  allocated_seats: number;
  is_active: boolean;
  settings?: {
    maxLicenses?: number;
  };
  // Computed fields
  used_seats?: number;
  usage_percentage?: number;
  admin?: {
    full_name: string | null;
    email: string;
  } | null;
}

export function DepartmentManagement() {
  const { profile, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { setHeaderAction, setPageTitle, setPageSubtitle, setBackTo } = useAdminLayoutContext();
  const [searchParams] = useSearchParams();
  const companyIdParam = searchParams.get('company_id');
  const companyName = searchParams.get('company_name');

  // Determine which company to show departments for
  const targetCompanyId = companyIdParam || profile?.companyId;

  // Fetch departments with usage stats
  const { data: rawDepartments, loading: departmentsLoading, refetch } = useSupabaseQuery<any[]>(
    'departments',
    {
      select: '*, profiles(count)',
      filter: (q: any) => targetCompanyId ? q.eq('company_id', targetCompanyId) : q,
      order: { column: 'name', ascending: true },
      enabled: !!targetCompanyId,
    }
  );

  // Process departments to add usage stats
  const [departments, setDepartments] = useState<DepartmentWithUsage[]>([]);

  useEffect(() => {
    if (!rawDepartments) return;

    const processDepartments = async () => {
      const processed: DepartmentWithUsage[] = [];

      for (const dept of rawDepartments) {
        const usedSeats = dept.profiles?.[0]?.count || 0;
        const allocatedSeats = dept.allocated_seats || 0;
        const usagePercentage = allocatedSeats > 0 ? Math.round((usedSeats / allocatedSeats) * 100) : 0;

        // Fetch admin for this department (org_admin or dept_admin)
        let admin: { full_name: string | null; email: string } | null = null;
        const { data: adminData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('department_id', dept.id)
          .in('role', ['org_admin', 'dept_admin'])
          .limit(1)
          .maybeSingle();

        if (adminData) {
          admin = { full_name: adminData.full_name, email: adminData.email };
        }

        processed.push({
          id: dept.id,
          created_at: dept.created_at,
          company_id: dept.company_id,
          name: dept.name,
          allocated_seats: allocatedSeats,
          is_active: dept.is_active ?? true,
          settings: dept.settings,
          used_seats: usedSeats,
          usage_percentage: usagePercentage,
          admin,
        });
      }

      setDepartments(processed);
    };

    processDepartments();
  }, [rawDepartments]);

  const [showModal, setShowModal] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<DepartmentWithUsage | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    allocated_seats: 20,
    is_active: true,
  });

  // Set page title, back button, and header action
  useEffect(() => {
    // Set back button if viewing a specific company
    if (companyName) {
      setBackTo('/admin/companies');
    } else {
      setBackTo(null);
    }

    // Set dynamic page title
    setPageTitle(companyName ? `${companyName} - Avdelinger` : 'Avdelinger');

    // Set header action button
    setHeaderAction(
      <button
        onClick={() => {
          setFormData({ name: '', allocated_seats: 20, is_active: true });
          setEditingDepartment(null);
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
      >
        <Plus className="h-5 w-5" />
        Ny avdeling
      </button>
    );

    return () => {
      setPageTitle(null);
      setHeaderAction(null);
      setBackTo(null);
    };
  }, [setHeaderAction, setPageTitle, setBackTo, companyName, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetCompanyId) return;

    try {
      if (editingDepartment) {
        const { error } = await supabase
          .from('departments')
          .update({
            name: formData.name,
            allocated_seats: formData.allocated_seats,
            is_active: formData.is_active,
          })
          .eq('id', editingDepartment.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('departments')
          .insert({
            company_id: targetCompanyId,
            name: formData.name,
            allocated_seats: formData.allocated_seats,
            is_active: formData.is_active,
            settings: {},
          });

        if (error) throw error;
      }

      setShowModal(false);
      setEditingDepartment(null);
      resetForm();
      refetch();
    } catch (error: any) {
      console.error('Error saving department:', error);
      alert('Feil ved lagring: ' + error.message);
    }
  };

  const handleEdit = (department: DepartmentWithUsage) => {
    setEditingDepartment(department);
    setFormData({
      name: department.name,
      allocated_seats: department.allocated_seats || 20,
      is_active: department.is_active ?? true,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Er du sikker på at du vil slette denne avdelingen? Brukere vil bli fjernet fra avdelingen.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', id);

      if (error) throw error;
      refetch();
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Kunne ikke slette avdeling. Den kan ha tilknyttede brukere.');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      allocated_seats: 20,
      is_active: true,
    });
  };

  const getSeatStatusBadge = (dept: DepartmentWithUsage) => {
    const usedSeats = dept.used_seats || 0;
    const allocatedSeats = dept.allocated_seats || 0;
    const usagePercentage = dept.usage_percentage || 0;

    if (usedSeats >= allocatedSeats) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:text-red-400">
          <AlertTriangle className="h-3 w-3" />
          Full kapasitet
        </span>
      );
    } else if (usagePercentage >= 75) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <AlertTriangle className="h-3 w-3" />
          Naer full
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3" />
          Ledig kapasitet
        </span>
      );
    }
  };

  if (departmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400 dark:text-gray-500">Laster avdelinger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Departments table */}
      {departments.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 dark:bg-gray-800 rounded-lg shadow-sm border">
          <Building2 className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
          <p className="text-gray-500">Ingen avdelinger ennå</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Opprett din forste avdeling for å organisere brukere
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avdeling
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avdelingsadmin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Antall lisenser
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {departments.map((department) => (
                <tr key={department.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center gap-3 cursor-pointer hover:opacity-75"
                      onClick={() => navigate(`/admin/users?department_id=${department.id}&department_name=${encodeURIComponent(department.name)}`)}
                    >
                      <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <div className="font-medium text-green-600 hover:text-green-800">
                          {department.name}
                        </div>
                        {!department.is_active && (
                          <div className="flex items-center gap-1 text-xs text-amber-600">
                            <AlertTriangle className="h-3 w-3" />
                            Inaktiv
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {department.admin ? (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {department.admin.full_name || 'Ikke satt'}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{department.admin.email}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-sm">Ingen admin</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-full max-w-xs">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {department.used_seats || 0} / {department.allocated_seats || 0}
                        </span>
                        <span className="text-xs text-gray-500">
                          {department.usage_percentage || 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (department.usage_percentage || 0) >= 100
                              ? 'bg-red-500'
                              : (department.usage_percentage || 0) >= 75
                              ? 'bg-orange-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(department.usage_percentage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(department);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Rediger avdeling"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(department.id);
                        }}
                        className="text-red-600 hover:text-red-900"
                        title="Slett avdeling"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <div className="fixed top-0 left-0 w-screen h-screen bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {editingDepartment ? 'Rediger avdeling' : 'Ny avdeling'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingDepartment(null);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Department Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Avdelingsnavn *
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Skriv inn avdelingsnavn"
                />
              </div>

              {/* Allocated Seats */}
              <div>
                <label htmlFor="allocated_seats" className="block text-sm font-medium text-gray-700 mb-1">
                  Antall seats *
                  {!isSuperAdmin && (
                    <span className="text-xs text-gray-500 ml-2">(Kun superadmin kan endre)</span>
                  )}
                </label>
                <input
                  id="allocated_seats"
                  type="number"
                  required
                  min="1"
                  value={formData.allocated_seats}
                  onChange={(e) => setFormData({ ...formData, allocated_seats: parseInt(e.target.value) || 1 })}
                  disabled={!isSuperAdmin}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !isSuperAdmin ? 'bg-gray-100 cursor-not-allowed' : ''
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Standard er 20 seats. Dette kan justeres trinnvis av superadmin.
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  id="is_active"
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                  Aktiv avdeling
                </label>
              </div>

              {/* Warning if editing with users */}
              {editingDepartment && (editingDepartment.used_seats || 0) > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                  <div className="flex gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-medium">OBS!</p>
                      <p>
                        Denne avdelingen har {editingDepartment.used_seats} aktive brukere.
                        {formData.allocated_seats < (editingDepartment.used_seats || 0) && (
                          <span className="block mt-1 font-medium">
                            Ny seat-tildeling ({formData.allocated_seats}) er mindre enn antall aktive brukere!
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingDepartment(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-md hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center gap-2"
                >
                  <Check className="h-4 w-4" />
                  {editingDepartment ? 'Oppdater' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
